/**
 * DMARC Stripe Integration Utilities
 *
 * Shared functions for DMARC product Stripe integration to avoid duplication
 * between StateContext and DMARCContext.
 */

/**
 * Convert email volume to Stripe quantity for tiered pricing
 * FIXED: Now returns actual email volume for Stripe tiered pricing
 * @param {string} emailVolume - Email volume (e.g., '100,000')
 * @returns {number} Actual email volume for Stripe tiered pricing
 */
export const getStripeQuantityFromEmailVolume = (emailVolume) => {
  const volumeNumber = parseInt(emailVolume.replace(/,/g, ''));

  switch (volumeNumber) {
    case 100000:
      return 100000; // 100K emails = quantity 100000 for Stripe tiered pricing
    case 500000:
      return 500000; // 500K emails = quantity 500000 for Stripe tiered pricing
    case 1000000:
      return 1000000; // 1M emails = quantity 1000000 for Stripe tiered pricing
    case 5000000:
      return 5000000; // 5M emails = quantity 5000000 for Stripe tiered pricing
    default:
      throw new Error(`Unsupported email volume: ${emailVolume}`);
  }
};

/**
 * Get Stripe price ID for a DMARC product
 * @param {string} productId - The DMARC product ID ('dmarc-plus' or 'dmarc-premium')
 * @returns {string} Stripe price ID
 */
export const getStripePriceId = (productId) => {
  let priceId;

  switch (productId) {
    case 'dmarc-plus':
      priceId = process.env.STRIPE_DMARC_PLUS_PRICE;
      break;
    case 'dmarc-premium':
      priceId = process.env.STRIPE_DMARC_PREMIUM_PRICE;
      break;
    default:
      throw new Error(`Unknown DMARC product ID: ${productId}`);
  }

  // Debug logging
  console.log(`🔍 DMARC Stripe Price ID for ${productId}:`, {
    priceId,
    hasEnv: !!priceId,
  });

  if (!priceId) {
    console.error(`❌ No Stripe price ID found for DMARC product: ${productId}`);
    throw new Error(`No Stripe price ID configured for DMARC product: ${productId}`);
  }

  return priceId;
};

/**
 * Get Stripe product ID for a DMARC product
 * @param {string} productId - The DMARC product ID ('dmarc-plus' or 'dmarc-premium')
 * @returns {string} Stripe product ID
 */
export const getStripeProductId = (productId) => {
  let productStripeId;

  switch (productId) {
    case 'dmarc-plus':
      productStripeId = process.env.STRIPE_DMARC_PLUS_PRODUCT;
      break;
    case 'dmarc-premium':
      productStripeId = process.env.STRIPE_DMARC_PREMIUM_PRODUCT;
      break;
    default:
      throw new Error(`Unknown DMARC product ID: ${productId}`);
  }

  // Debug logging
  console.log(`🔍 DMARC Stripe Product ID for ${productId}:`, {
    productStripeId,
    hasEnv: !!productStripeId,
  });

  if (!productStripeId) {
    console.error(`❌ No Stripe product ID found for DMARC product: ${productId}`);
    throw new Error(`No Stripe product ID configured for DMARC product: ${productId}`);
  }

  return productStripeId;
};
